import { getDataTable } from '../../features/visual-analytics/charts/chartDataBindings';
import { convertColumnDataToDisplayString } from '../../features/visual-analytics/helpers/misc';
import { getColumnInfo } from '../analyticsDataSlice';
import { TpCanonicalTypeInfo, TpDataContext } from '../interfaces';
import {
  TpActiveEnSelectionFilter,
  TpEnSelectionFilterDefinition,
  TpEnSelectionFilterStep,
  TpEnSelectionFilterStepIntersections,
  TpEnSelectionFilterTypes,
} from './interfaces';
import { getEnSelectionFilterStep, removeEnSelectionFilterStep, smartRenderEnFilterItems } from './selectionEnFilters';
import { TpUserEnSelectionAction, TpUserEnSelectionActionIntersections } from './userSelectionActions';

function testArrayEquals(a: any[], b: any[]): boolean {
  return Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((val, index) => val === b[index]);
}

export function testIntersectionEquals(a: any[], b: any[]): boolean {
  return testArrayEquals(a.slice().sort(), b.slice().sort());
}

function getIntersectionsEnFilterStepKey(dataTableId: string, colId: string): string {
  return `intersectionsFilterStep_${dataTableId}_${colId}`;
}

export function getIntersectionslEnFilterStep(
  selectionFilter: TpActiveEnSelectionFilter,
  dataTableId: string,
  colId: string,
): TpEnSelectionFilterStepIntersections | null {
  const step = getEnSelectionFilterStep(selectionFilter, getIntersectionsEnFilterStepKey(dataTableId, colId));
  if (!step) return null;
  return step as TpEnSelectionFilterStepIntersections;
}

function handleUserAction(
  state: any,
  dataContext: TpDataContext,
  canonicalTypeInfo: TpCanonicalTypeInfo,
  userSelectionAction: TpUserEnSelectionAction,
) {
  const userSelectionActionIntersections = userSelectionAction as TpUserEnSelectionActionIntersections;
  const key = getIntersectionsEnFilterStepKey(
    userSelectionActionIntersections.dataTableId,
    userSelectionActionIntersections.colId,
  );
  const step = getEnSelectionFilterStep(canonicalTypeInfo.selectionFilter, key);
  let stepIntersections: TpEnSelectionFilterStepIntersections;
  if (!step) {
    // step not yet present - we create it
    stepIntersections = {
      type: TpEnSelectionFilterTypes.INTERSECTIONS,
      key,
      dataTableId: userSelectionActionIntersections.dataTableId,
      colId: userSelectionActionIntersections.colId,
      intersections: [userSelectionActionIntersections.setIds],
    };
    canonicalTypeInfo.selectionFilter.steps.push(stepIntersections);
  } else {
    // step already present - we modify it
    stepIntersections = step as TpEnSelectionFilterStepIntersections;
    if (
      stepIntersections.intersections.some((intersection) =>
        testIntersectionEquals(intersection, userSelectionActionIntersections.setIds),
      )
    ) {
      // item is present -  we remove it
      stepIntersections.intersections = stepIntersections.intersections.filter(
        (item) => !testIntersectionEquals(item, userSelectionActionIntersections.setIds),
      );
      if (stepIntersections.intersections.length == 0) {
        // since no more items are left, we remove the entire filter step
        removeEnSelectionFilterStep(canonicalTypeInfo.selectionFilter, key);
      }
    } else {
      // item not yet present -  we add it
      stepIntersections.intersections.push(userSelectionActionIntersections.setIds);
    }
  }
}

function performStep(
  dataContext: TpDataContext,
  canonicalTypeInfo: TpCanonicalTypeInfo,
  step: TpEnSelectionFilterStep,
): any[] {
  const stepIntersections = step as TpEnSelectionFilterStepIntersections;
  const dataTable = getDataTable(dataContext, stepIntersections.dataTableId);
  const colInfo = getColumnInfo(dataTable, stepIntersections.colId);
  const itemIdList = dataTable.columnData[dataTable.colItemId];
  const catValList = dataTable.columnData[stepIntersections.colId];
  const selectedItemIds: string[] = [];

  for (let itemIdx = 0; itemIdx < itemIdList.length; itemIdx++) {
    const itemSetIds = catValList[itemIdx];
    let isMatch = false;
    for (const intersection of stepIntersections.intersections)
      if (testIntersectionEquals(intersection.slice().sort(), itemSetIds)) isMatch = true;
    if (isMatch) selectedItemIds.push(itemIdList[itemIdx]);
  }

  return selectedItemIds;
}

function renderContent(
  dataContext: TpDataContext,
  canonicalTypeInfo: TpCanonicalTypeInfo,
  step: TpEnSelectionFilterStep,
  forceString?: boolean,
) {
  const stepIntersections = step as TpEnSelectionFilterStepIntersections;
  const dataTable = getDataTable(dataContext, stepIntersections.dataTableId);
  const colInfo = getColumnInfo(dataTable, stepIntersections.colId);
  const missingValue = '(unknown value)';
  const valuesList = stepIntersections.intersections.map((intersection) => {
    return intersection.map((catId) => convertColumnDataToDisplayString(colInfo, catId) ?? missingValue).join(' + ');
  });

  if (forceString) return `${colInfo.name}=${smartRenderEnFilterItems(valuesList, true)}`;

  return (
    <span>
      <b>{colInfo.name}</b> = {smartRenderEnFilterItems(valuesList)}
    </span>
  );
}

export const defEnSelectionFilterIntersections: TpEnSelectionFilterDefinition = {
  handleUserAction,
  performStep,
  renderContent,
};
